import React, { memo, useCallback, useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { areEqual } from 'Utils/equalityChecks';
import { Api } from 'Utils/api';

import { MergeProjectModal } from 'Components/Project';
import { projectSelector, projectCompanyIdSelector } from 'Containers/RocketScan/selectors';
import { setToaster } from 'Containers/Core/actions';

interface Props {
  isOpen: boolean;
  setIsOpen: (e: any) => void;
  closeMenu: () => void;
}

interface SourceProject {
  id: number;
  uid: string;
  alias: string;
  address?: any;
}

interface PreviewData {
  counts: Record<string, number>;
  room_mappings: Array<{
    source_room: string;
    target_room: string;
    is_new: boolean;
    room_type_name: string;
    level_name: string;
    photo_count: number;
  }>;
  warnings: string[];
  template_data_not_moved?: Record<string, number>;
}

const MergeProjectModalContainer = ({ isOpen, setIsOpen, closeMenu }: Props) => {
  const dispatch = useDispatch();
  const project = useSelector(projectSelector, areEqual);
  const companyId = useSelector(projectCompanyIdSelector, areEqual);

  const [step, setStep] = useState<'select' | 'preview' | 'merging' | 'done'>('select');
  const [sourceSearch, setSourceSearch] = useState('');
  const [recentProjects, setRecentProjects] = useState<SourceProject[]>([]);
  const [sourceProjects, setSourceProjects] = useState<SourceProject[]>([]);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedSource, setSelectedSource] = useState<SourceProject | null>(null);
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [mergeError, setMergeError] = useState('');
  const [confirmationString, setConfirmationString] = useState('');

  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>(null);
  const reloadTimerRef = useRef<ReturnType<typeof setTimeout>>(null);
  const mountedRef = useRef(true);
  const isOpenRef = useRef(isOpen);
  const previewRequestRef = useRef(0);
  const searchRequestRef = useRef(0);

  useEffect(
    () => () => {
      mountedRef.current = false;
      if (reloadTimerRef.current) clearTimeout(reloadTimerRef.current);
    },
    []
  );

  // Keep isOpenRef in sync and reset state when modal closes
  useEffect(() => {
    isOpenRef.current = isOpen;

    if (!isOpen) {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
        searchTimerRef.current = null;
      }
      if (reloadTimerRef.current) {
        clearTimeout(reloadTimerRef.current);
        reloadTimerRef.current = null;
      }
      // Bump request tokens so any in-flight responses are ignored
      previewRequestRef.current += 1;
      searchRequestRef.current += 1;

      setStep('select');
      setSourceSearch('');
      setRecentProjects([]);
      setSourceProjects([]);
      setDropdownLoading(false);
      setDropdownOpen(false);
      setSelectedSource(null);
      setPreview(null);
      setPreviewLoading(false);
      setPreviewError('');
      setMergeError('');
      setConfirmationString('');
    }
  }, [isOpen]);

  const fetchRecentProjects = useCallback(async () => {
    if (!companyId) return;

    searchRequestRef.current += 1;
    const requestToken = searchRequestRef.current;

    setDropdownLoading(true);

    try {
      const response = await Api.get(`companies/${companyId}/projects`, {
        params: {
          include: 'address',
          sort: '-created_at',
        },
      });

      if (!mountedRef.current || !isOpenRef.current) return;
      if (searchRequestRef.current !== requestToken) return;

      const projects = response?.data?.data || [];
      const filtered = projects.filter((p: any) => p.id !== project.id).slice(0, 5);
      setRecentProjects(filtered);
      setDropdownOpen(true);
    } catch {
      if (!mountedRef.current || !isOpenRef.current) return;
      if (searchRequestRef.current !== requestToken) return;
      setRecentProjects([]);
      setDropdownOpen(false);
    } finally {
      if (mountedRef.current && isOpenRef.current && searchRequestRef.current === requestToken) {
        setDropdownLoading(false);
      }
    }
  }, [companyId, project]);

  useEffect(() => {
    if (isOpen) {
      fetchRecentProjects();
    }
  }, [isOpen, fetchRecentProjects]);

  // Debounced project search
  const searchProjects = useCallback(
    (term: string) => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
        searchTimerRef.current = null;
      }

      // Invalidate any in-flight request before early return
      searchRequestRef.current += 1;

      if (!companyId || term.length < 2) {
        setSourceProjects([]);
        setDropdownOpen(true);
        setDropdownLoading(false);
        return;
      }

      const requestToken = searchRequestRef.current;
      setDropdownLoading(true);

      searchTimerRef.current = setTimeout(async () => {
        try {
          const response = await Api.get(`companies/${companyId}/projects`, {
            params: {
              'filter[search]': term,
              include: 'address',
            },
          });

          if (!mountedRef.current || !isOpenRef.current) return;
          if (searchRequestRef.current !== requestToken) return;

          const projects = response?.data?.data || [];
          // Exclude current project from results
          const filtered = projects.filter((p: any) => p.id !== project.id);
          setSourceProjects(filtered);
          setDropdownOpen(filtered.length > 0 || term.length >= 2);
        } catch {
          if (!mountedRef.current || !isOpenRef.current) return;
          if (searchRequestRef.current !== requestToken) return;
          setSourceProjects([]);
          setDropdownOpen(false);
        } finally {
          if (mountedRef.current && isOpenRef.current && searchRequestRef.current === requestToken) {
            setDropdownLoading(false);
          }
        }
      }, 300);
    },
    [companyId, project]
  );

  const onSearchChange = useCallback(
    (e: any) => {
      const { value } = e.target;
      setSourceSearch(value);
      searchProjects(value);
    },
    [searchProjects]
  );

  const onSearchFocus = useCallback(() => {
    setDropdownOpen(true);

    if (sourceSearch.length < 2 && recentProjects.length === 0 && !dropdownLoading) {
      fetchRecentProjects();
    }
  }, [sourceSearch, recentProjects, dropdownLoading, fetchRecentProjects]);

  const fetchPreview = useCallback(
    async (sourceId: number) => {
      if (!project?.id) return;

      previewRequestRef.current += 1;
      const requestToken = previewRequestRef.current;

      setPreviewLoading(true);
      setPreviewError('');

      try {
        const response = await Api.get(`projects/${project.id}/merge-preview`, {
          params: { source_id: sourceId },
        });

        if (!mountedRef.current || !isOpenRef.current) return;
        if (previewRequestRef.current !== requestToken) return;

        setPreview(response?.data?.data || response?.data);
        setStep('preview');
      } catch (error: any) {
        if (!mountedRef.current || !isOpenRef.current) return;
        if (previewRequestRef.current !== requestToken) return;

        const message = error?.response?.data?.message || error?.response?.data?.error || 'Failed to load preview.';
        setPreviewError(message);
      } finally {
        if (mountedRef.current && isOpenRef.current && previewRequestRef.current === requestToken) {
          setPreviewLoading(false);
        }
      }
    },
    [project]
  );

  const onSelectSource = useCallback(
    (sourceProject: any) => {
      // Invalidate any in-flight search so stale results don't reopen dropdown
      searchRequestRef.current += 1;
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
        searchTimerRef.current = null;
      }
      setSelectedSource(sourceProject);
      setDropdownOpen(false);
      setSourceSearch('');
      setPreview(null);
      setPreviewError('');
      setConfirmationString('');
      fetchPreview(sourceProject.id);
    },
    [fetchPreview]
  );

  const onClearSource = useCallback(() => {
    // Invalidate any in-flight preview
    previewRequestRef.current += 1;
    setSelectedSource(null);
    setPreview(null);
    setPreviewError('');
    setPreviewLoading(false);
    setStep('select');
    setConfirmationString('');
    setDropdownOpen(true);
  }, []);

  const onBackClick = useCallback(() => {
    setStep('select');
    setMergeError('');
    setConfirmationString('');
  }, []);

  const onMergeClick = useCallback(
    async (e: any) => {
      e.preventDefault();

      if (!selectedSource || !project?.id) return;

      setStep('merging');
      setMergeError('');

      try {
        await Api.post(`projects/${project.id}/merge`, {
          source_project_id: selectedSource.id,
        });

        if (!mountedRef.current || !isOpenRef.current) return;

        setStep('done');
        dispatch(setToaster('Projects merged successfully', true));

        // Reload after a brief delay so the user sees the success state
        reloadTimerRef.current = setTimeout(() => {
          window.location.reload();
        }, 1500);
      } catch (error: any) {
        if (!mountedRef.current || !isOpenRef.current) return;

        const message =
          error?.response?.data?.message || error?.response?.data?.error || 'Merge failed. Please try again.';
        setMergeError(message);
        setStep('preview');
      }
    },
    [selectedSource, project, dispatch]
  );

  const onCloseClick = useCallback(() => {
    setIsOpen(false);
    closeMenu();
  }, [setIsOpen, closeMenu]);

  const onChangeConfirmationString = useCallback((e: any) => {
    setConfirmationString(e.target.value);
  }, []);

  return (
    <MergeProjectModal
      isOpen={isOpen}
      step={step}
      sourceSearch={sourceSearch}
      recentProjects={recentProjects}
      sourceProjects={sourceProjects}
      dropdownLoading={dropdownLoading}
      dropdownOpen={dropdownOpen}
      selectedSource={selectedSource}
      preview={preview}
      previewLoading={previewLoading}
      previewError={previewError}
      mergeError={mergeError}
      confirmationString={confirmationString}
      targetUid={project?.uid || ''}
      onSearchChange={onSearchChange}
      onSearchFocus={onSearchFocus}
      onSelectSource={onSelectSource}
      onClearSource={onClearSource}
      onBackClick={onBackClick}
      onMergeClick={onMergeClick}
      onCloseClick={onCloseClick}
      onChangeConfirmationString={onChangeConfirmationString}
    />
  );
};

const MergeProjectModalContainerMemo = memo(MergeProjectModalContainer, areEqual);

export { MergeProjectModalContainerMemo as MergeProjectModal };
